import { createRouter, createWebHistory } from 'vue-router'
import type { NavigationGuardNext, RouteLocationNormalized, RouteRecordNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useProjectsStore } from '@/stores/projects'
import { useNavigationHistoryStore } from '@/stores/navigationHistory'
import { notifyError } from '@/lib/notifications'
import { ensureRecorderForAllowedRoute, disableRecorder, saveVisitorData } from '@/lib/revisitRecorderClient'
const workflowsEnabled = ['1', 'true', 'yes', 'on'].includes(
  String((import.meta as any).env?.VITE_FEATURE_WORKFLOWS ?? 'true').toLowerCase()
)

// Superuser bypass email - grants access to all projects
const SUPERUSER_EMAILS = ['egykettoharo@gmail.com', 'matthiasvangorp@gmail.com']
const isSuperuser = (user: any): boolean => {
  if (!user?.email) return false
  return SUPERUSER_EMAILS.includes(user.email)
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    return { left: 0, top: 0 }
  },
  routes: [
    // Public routes
    {
      path: '/',
      redirect: { name: 'login' }
    },
    {
      path: '/features/heatmaps',
      name: 'feature-heatmaps',
      component: () => import('@/views/marketing/features/Heatmaps.vue')
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/ContactView.vue')
    },
    // Help Center (public)
    {
      path: '/helpcenter',
      component: () => import('@/views/helpcenter/HelpCenterLayout.vue'),
      children: [
        {
          path: '',
          name: 'helpcenter-home',
          component: () => import('@/views/helpcenter/HelpCenterHome.vue')
        },
        {
          path: 'integrations',
          name: 'helpcenter-integrations',
          component: () => import('@/views/helpcenter/integrations/IntegrationsHome.vue')
        },
        {
          path: 'integrations/slack',
          name: 'helpcenter-integrations-slack',
          component: () => import('@/views/helpcenter/integrations/Slack.vue')
        },
        {
          path: 'integrations/jira',
          name: 'helpcenter-integrations-jira',
          component: () => import('@/views/helpcenter/integrations/Jira.vue')
        },
        {
          path: 'integrations/github',
          name: 'helpcenter-integrations-github',
          component: () => import('@/views/helpcenter/integrations/Github.vue')
        },
        {
          path: 'integrations/zendesk',
          name: 'helpcenter-integrations-zendesk',
          component: () => import('@/views/helpcenter/integrations/Zendesk.vue')
        },
        // Existing help center articles (slug-based)
        {
          path: ':article',
          name: 'helpcenter-article',
          component: () => import('@/views/helpcenter/HelpArticleView.vue'),
        }
      ]
    },
    // Blog routes
    {
      path: '/blog',
      name: 'blog',
      component: () => import('@/views/blog/BlogIndex.vue')
    },
    {
      path: '/blog/best-product-analytics-platform-for-web-services',
      name: 'blog-product-analytics-guide', 
      component: () => import('@/views/blog/posts/best-product-analytics-platform-for-web-services.vue')
    },
    {
      path: '/blog/welcome-to-revisit-blog',
      name: 'blog-welcome',
      component: () => import('@/views/blog/posts/welcome-to-revisit-blog.vue')
    },
    {
      path: '/blog/why-session-replays-alone-arent-enough',
      name: 'blog-ai-session-analysis',
      component: () => import('@/views/blog/posts/why-session-replays-alone-arent-enough.vue')
    },
    {
      path: '/blog/revisit-vs-hotjar-vs-fullstory',
      name: 'blog-comparison-guide',
      component: () => import('@/views/blog/posts/revisit-vs-hotjar-vs-fullstory.vue')
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/mfa',
      name: 'mfa',
      component: () => import('@/views/auth/MfaView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/jobs',
      name: 'queue-monitor',
      component: () => import('@/views/QueueMonitorView.vue')
    },
    {
      path: '/email-testing',
      name: 'email-testing',
      component: () => import('@/views/EmailTestingView.vue'),
      meta: { testDomainOnly: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/auth/RegisterView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/invitations/:token',
      name: 'invitation-register',
      component: () => import('@/views/auth/InvitationRegisterView.vue'),
      meta: { requiresGuest: true },
      props: true
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/auth/ForgotPasswordView.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: () => import('@/views/auth/ResetPasswordView.vue'),
      meta: { requiresGuest: true },
      props: true
    },
    {
      path: '/verify-email/:token',
      name: 'verify-email',
      component: () => import('@/views/auth/VerifyEmailView.vue'),
      meta: { allowUnverified: true },
      props: true
    },
    {
      path: '/onboarding/first-project',
      name: 'first-project-onboarding',
      component: () => import('@/views/ProjectOnboardingView.vue'),
      meta: { requiresAuth: true, allowUnverified: true }
    },
    {
      path: '/verify-reminder',
      name: 'verify-reminder',
      component: () => import('@/views/auth/VerifyReminderView.vue'),
      meta: { requiresAuth: true, allowUnverified: true }
    },
    {
      path: '/renderer-debug/:id/:sessionId',
      name: 'renderer-debug',
      component: () => import('@/views/project/RendererDebugView.vue'),
      meta: { public: true }, // No auth required - uses token for API calls
      props: true
    },
    // Public shared session viewer
    {
      path: '/share/:token',
      name: 'shared-session',
      component: () => import('@/views/public/SharedSessionView.vue'),
      props: true
    },
    // Public policies
    {
      path: '/terms',
      name: 'terms',
      component: () => import('@/views/public/TermsView.vue')
    },
    {
      path: '/dpa',
      name: 'dpa',
      component: () => import('@/views/public/DpaView.vue')
    },
    {
      path: '/privacy',
      name: 'privacy-policy',
      component: () => import('@/views/public/PrivacyPolicyView.vue')
    },
    {
      path: '/cookies',
      name: 'cookies-policy',
      component: () => import('@/views/public/CookiesPolicyView.vue')
    },
    {
      path: '/privacy/california',
      name: 'privacy-california',
      component: () => import('@/views/public/CaliforniaPrivacyNoticeView.vue')
    },
    {
      path: '/subprocessors',
      name: 'subprocessors',
      component: () => import('@/views/public/SubprocessorListView.vue')
    },
    {
      path: '/acceptable-use',
      name: 'acceptable-use',
      component: () => import('@/views/public/AcceptableUsePolicyView.vue')
    },
    {
      path: '/security',
      name: 'security',
      component: () => import('@/views/public/SecurityView.vue')
    },
    // Marketing Feature Pages
    // (moved to top)
    // Protected routes
    // Protected routes
    // Removed global dashboard in favor of per-project dashboards
    {
      path: '/projects',
      name: 'projects',
      component: () => import('@/views/ProjectsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/projects/new',
      name: 'project-onboarding',
      component: () => import('@/views/ProjectOnboardingView.vue'),
      meta: { requiresAuth: true, allowUnverified: true }
    },
    {
      path: '/projects/:id',
      component: () => import('@/views/project/ProjectLayout.vue'),
      meta: { requiresAuth: true },
      props: true,
      children: [
        {
          path: '',
          name: 'project-dashboard',
          component: () => import('@/views/project/ProjectDashboardView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'sessions',
          component: () => import('@/views/project/ProjectSessionsWrapper.vue'),
          meta: { requiresAuth: true },
          children: [
            { path: '', name: 'project-sessions', component: () => import('@/views/project/ProjectSessionsView.vue'), meta: { requiresAuth: true } },
            { path: 'live', name: 'project-live-sessions', component: () => import('@/views/project/LiveSessionsView.vue'), meta: { requiresAuth: true } },
            { path: 'categories', name: 'project-categories', component: () => import('@/views/project/ProjectCategoriesView.vue'), meta: { requiresAuth: true } },
            { path: 'categories/:categoryId', name: 'project-category-view', component: () => import('@/views/project/CategorySessionsView.vue'), meta: { requiresAuth: true }, props: true },
          ]
        },
        {
          path: 'visitors',
          name: 'project-visitors',
          component: () => import('@/views/project/ProjectVisitorsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'visitors/:visitorId',
          name: 'project-visitor-view',
          component: () => import('@/views/project/VisitorView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'sessions/:sessionId',
          name: 'project-session-view',
          component: () => import('@/views/project/SessionView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'sessions/:sessionId/live',
          name: 'project-session-live-view',
          component: () => import('@/views/project/LiveSessionView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'events',
          name: 'project-events',
          component: () => import('@/views/project/ProjectEventsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'events/:eventId/analytics',
          name: 'project-event-analytics',
          component: () => import('@/views/project/EventAnalyticsView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'funnels',
          name: 'project-funnels',
          component: () => import('@/views/project/ProjectFunnelsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'funnels/new',
          name: 'project-funnel-new',
          component: () => import('@/views/project/FunnelBuilderView.vue'),
          meta: { requiresAuth: true },
          props: { funnelId: 'new' }
        },
        {
          path: 'funnels/:funnelId/edit',
          name: 'project-funnel-edit',
          component: () => import('@/views/project/FunnelBuilderView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'funnels/:funnelId/analytics',
          name: 'project-funnel-analytics',
          component: () => import('@/views/project/FunnelAnalyticsView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'chat/:chatId?',
          // Legacy chat route (deprecated). We no longer use /chat routes; AI chat lives in the Ask AI sidebar.
          // Keep as a redirect so old links still work, but URL will move away from /chat.
          name: 'project-chat-legacy',
          meta: { requiresAuth: true },
          redirect: (to: any) => {
            const pid = String((to.params as any)?.id || '')
            const convId = (to.params as any)?.chatId ? String((to.params as any).chatId) : ''
            const nextQuery: any = { ...(to.query || {}), ai: '1' }
            if (convId) nextQuery.aiConv = convId
            return { name: 'project-dashboard', params: { id: pid }, query: nextQuery }
          },
        },
        {
          path: 'heatmaps',
          name: 'project-heatmaps',
          component: () => import('@/views/project/ProjectHeatmapsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'heatmaps/:heatmapId',
          name: 'project-heatmap-view',
          component: () => import('@/views/project/HeatmapView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'search-console',
          name: 'project-search-console',
          component: () => import('@/views/project/ProjectSearchConsoleView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'errors',
          name: 'project-errors',
          component: () => import('@/views/project/ProjectErrorsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'errors/:fingerprint',
          name: 'project-error-detail',
          component: () => import('@/views/project/ErrorDetailView.vue'),
          meta: { requiresAuth: true },
          props: true
        },
        {
          path: 'members',
          name: 'project-members',
          component: () => import('@/views/project/settings/ProjectMembersView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'settings',
          component: () => import('@/views/project/ProjectSettingsView.vue'),
          meta: { requiresAuth: true },
          children: [
            { path: '', redirect: { name: 'project-settings-general' } },
            {
              path: 'general',
              name: 'project-settings-general',
              component: () => import('@/views/project/settings/ProjectSettingsGeneralView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'masking',
              name: 'project-settings-masking',
              component: () => import('@/views/project/settings/ProjectSettingsMaskingView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'ip-exclusion',
              name: 'project-settings-ip-exclusion',
              component: () => import('@/views/project/settings/ProjectSettingsIpExclusionView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'members',
              name: 'project-settings-members',
              component: () => import('@/views/project/settings/ProjectMembersView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'tracking',
              name: 'project-settings-tracking',
              component: () => import('@/views/project/settings/ProjectSettingsTrackingView.vue'),
              meta: { requiresAuth: true }
            }
            ,
            {
              path: 'ai-insights',
              name: 'project-settings-ai-insights',
              component: () => import('@/views/project/settings/ProjectSettingsAiInsightsView.vue'),
              meta: { requiresAuth: true }
            }
            ,
            {
              path: 'ai-usage',
              name: 'project-settings-ai-usage',
              component: () => import('@/views/project/settings/ProjectSettingsAiUsageView.vue'),
              meta: { requiresAuth: true }
            }
            ,
            {
              path: 'billing',
              name: 'project-settings-billing',
              component: () => import('@/views/project/settings/ProjectSettingsBillingView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'emails',
              name: 'project-settings-emails',
              component: () => import('@/views/project/settings/ProjectSettingsEmailsView.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'integrations',
              name: 'project-settings-integrations',
              component: () => import('@/views/project/settings/ProjectSettingsIntegrationsView.vue'),
              meta: { requiresAuth: true }
            },
            ...(workflowsEnabled ? [
              {
                path: 'workflows',
                name: 'project-settings-workflows',
                component: () => import('@/views/project/settings/ProjectSettingsWorkflowsView.vue'),
                meta: { requiresAuth: true }
              },
              {
                path: 'workflows/workshop',
                name: 'project-settings-workflow-workshop',
                component: () => import('@/views/project/settings/ProjectSettingsWorkflowWorkshopView.vue'),
                meta: { requiresAuth: true }
              },
              {
                path: 'workflows/workshop/:itemId',
                name: 'project-settings-workflow-workshop-item',
                component: () => import('@/views/project/settings/workflow-workshop/WorkshopItemDetailView.vue'),
                meta: { requiresAuth: true },
                props: true
              },
              {
                path: 'workflows/:workflowId',
                name: 'project-settings-workflow-edit',
                component: () => import('@/views/project/settings/WorkflowBuilderView.vue'),
                meta: { requiresAuth: true },
                props: true
              }
            ] : [])
          ]
        }
      ]
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    // 404 page
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue')
    }
  ]
})

const RECORDER_ALLOWED_ROUTE_NAMES = new Set<string>([
  'login',
  'register',
  'projects',
  'project-onboarding',
  'first-project-onboarding'
])

// router.afterEach((to) => {
//   if (typeof window === 'undefined') return

//   // Never record inside specific project views like /projects/:id/*
//   const isProjectDetailRoute = to.matched.some((record: RouteRecordNormalized) =>
//     String(record.path || '').startsWith('/projects/:id')
//   )


//   if (isProjectDetailRoute) {
//     disableRecorder()
//     return
//   }

//   const name = String(to.name || '')
//   if (RECORDER_ALLOWED_ROUTE_NAMES.has(name)) {
//     void ensureRecorderForAllowedRoute(name).then(() => {
//       const authStore = useAuthStore()
//       if (authStore.user) {
//         saveVisitorData(authStore.user)
//       }
//     })
//   } else {
//     disableRecorder()
//   }
// })

// Navigation guards
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const authStore = useAuthStore()
  const projectsStore = useProjectsStore()

  // Check if route is restricted to .test domains only
  const testDomainOnly = to.matched.some((record: RouteRecordNormalized) => (record.meta as any).testDomainOnly)
  if (testDomainOnly) {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    if (!hostname.endsWith('.test')) {
      next({ name: 'not-found' })
      return
    }
  }

  // Attempt to initialize auth when the destination requires auth or guest
  const requiresAuth = to.matched.some((record: RouteRecordNormalized) => (record.meta as any).requiresAuth)
  const requiresGuest = to.matched.some((record: RouteRecordNormalized) => (record.meta as any).requiresGuest)
  if ((requiresAuth || requiresGuest) && authStore.user == null) {
    try {
      await authStore.init()
    } catch (error) {
      console.error('Failed to initialize auth:', error)
      // If init fails, try one more time after a brief delay
      try {
        await new Promise(resolve => setTimeout(resolve, 100))
        await authStore.init()
      } catch (retryError) {
        console.error('Retry auth init failed:', retryError)
      }
    }
  }

  // Check if route requires authentication
  if (requiresAuth) {
    if (!authStore.isAuthenticated) {
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
      return
    }

    // Special handling for first-project-onboarding route (check before allowUnverified)
    if ((to.name as string) === 'first-project-onboarding') {
      console.log('[router] Checking first-project-onboarding access')
      // Check if user already has projects
      try {
        await projectsStore.fetchProjects()
        const hasProjects = projectsStore.projects.length > 0
        console.log('[router] User has projects:', hasProjects, 'count:', projectsStore.projects.length)

        if (hasProjects) {
          // User already has projects, redirect based on verification
          console.log('[router] User has projects, verified:', authStore.isVerified)
          if (authStore.isVerified) {
            console.log('[router] Redirecting to projects (verified with projects)')
            next({ name: 'projects' })
          } else {
            console.log('[router] Redirecting to verify-reminder (unverified with projects)')
            next({ name: 'verify-reminder' })
          }
          return
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err)
      }

      // No projects, allow access to first-project onboarding
      console.log('[router] No projects, allowing first-project-onboarding access')
      next()
      return
    }

    // Special handling for /projects route
    if ((to.name as string) === 'projects') {
      // Check for pending invitations
      let hasPendingInvites = false
      try {
        const inv = await authStore.listPendingInvitations()
        hasPendingInvites = inv?.success && Array.isArray((inv as any).data) && (inv as any).data.length > 0
      } catch (err) {
        console.error('Failed to check pending invitations:', err)
      }

      // If not verified and no invitations, redirect to verify-reminder
      if (!authStore.isVerified && !hasPendingInvites) {
        next({ name: 'verify-reminder' })
        return
      }

      // If verified, check if user has projects
      if (authStore.isVerified) {
        try {
          await projectsStore.fetchProjects()
          const hasProjects = projectsStore.projects.length > 0

          // If no projects and no invitations, redirect to first-project onboarding
          if (!hasProjects && !hasPendingInvites) {
            next({ name: 'first-project-onboarding' })
            return
          }
        } catch (err) {
          console.error('Failed to fetch projects:', err)
        }
      }

      // Allow access to projects page
      next()
      return
    }

    // Handle unverified users for other routes
    const allowUnverified = to.matched.some((record: RouteRecordNormalized) => (record.meta as any).allowUnverified)

    if (!authStore.isVerified && !allowUnverified) {
      // If the user has pending invitations, always allow access to projects
      // Check invitations first before redirecting
      let hasPendingInvites = false
      try {
        const inv = await authStore.listPendingInvitations()
        hasPendingInvites = inv?.success && Array.isArray((inv as any).data) && (inv as any).data.length > 0
      } catch (err) {
        console.error('Failed to check pending invitations:', err)
      }

      if (hasPendingInvites) {
        // Redirect to projects to show invitations
        next({ name: 'projects' })
        return
      }

      // Check if user needs to create first project or just verify
      const allowedRoutes = ['first-project-onboarding', 'verify-reminder', 'project-onboarding']

      if (!allowedRoutes.includes(to.name as string)) {
        // Check if user has projects
        let hasProjects = false
        try {
          await projectsStore.fetchProjects()
          hasProjects = projectsStore.projects.length > 0
        } catch (err) {
          console.error('Failed to fetch projects:', err)
        }

        // Redirect unverified users
        if (hasProjects) {
          // Has projects but unverified - redirect to verify reminder
          next({ name: 'verify-reminder' })
        } else {
          // No projects - redirect to first project onboarding
          next({ name: 'first-project-onboarding' })
        }
        return
      }
    }

    // Check if email verification is required for certain routes
    if (to.matched.some((record: RouteRecordNormalized) => (record.meta as any).requiresVerification) && !authStore.isVerified) {
      next({
        name: 'verify-email',
        query: { message: 'Please verify your email address to continue' }
      })
      return
    }
  }

  // Check if route requires guest (unauthenticated user)
  if (to.matched.some((record: RouteRecordNormalized) => (record.meta as any).requiresGuest)) {
    if (authStore.isAuthenticated) {
      next({ name: 'projects' })
      return
    }
  }

  // Project membership guard: restrict /projects/:id/* to owner or member
  try {
    const projectRoute = to.matched.find((r: RouteRecordNormalized) => String(r.path || '').startsWith('/projects/:id'))
    const pid = to.params && (to.params as any).id ? String((to.params as any).id) : ''
    if (projectRoute && pid) {
      // If currentProject already matches, allow
      const cp = projectsStore.currentProject
      if (!cp || cp.id !== pid) {
        const res = await projectsStore.getProject(pid).catch((e: any) => ({ success: false, message: e?.message || 'Project not found' }))
        // getProject throws on non-success; handle normalized result as well
        if (!res || (res as any).success === false) {
          try { notifyError('Project does not exist or you do not have access.') } catch { }
          next({ name: 'projects', query: { error: 'project-not-found' } })
          return
        }
      }

      // Owner-only access to Settings and Members views (except email preferences which are per-user)
      // Superuser bypass allows access regardless of ownership
      const routeName = String(to.name || '')
      const isEmailsRoute = routeName === 'project-settings-emails'
      const isSettingsRoute = (routeName.startsWith('project-settings-') || routeName === 'project-members' || routeName === 'project-settings-members') && !isEmailsRoute
      if (isSettingsRoute) {
        const current = projectsStore.currentProject
        const userId = (authStore.user && (authStore.user as any).id) ? String((authStore.user as any).id) : ''
        const ownerId = current && (current as any).ownerId ? String((current as any).ownerId) : ''
        const isOwner = userId && ownerId && userId === ownerId
        if (!isOwner && !isSuperuser(authStore.user)) {
          try { notifyError('Only project owner can access settings.') } catch { }
          next({ name: 'project-dashboard', params: { id: pid } })
          return
        }
      }
    }
  } catch (e) {
    // If something goes wrong while checking, fail closed
    try { notifyError('Project does not exist or you do not have access.') } catch { }
    next({ name: 'projects', query: { error: 'project-not-found' } })
    return
  }

  next()
})

// Track recent project pages for the command palette (per-project, last 10)
router.afterEach((to, from) => {
  if (typeof window === 'undefined') return
  try {
    const isProjectDetailRoute = to.matched.some((record: RouteRecordNormalized) =>
      String(record.path || '').startsWith('/projects/:id')
    )
    if (!isProjectDetailRoute) return

    const pidRaw = (to.params as any)?.id
    const projectId = typeof pidRaw === 'string' ? pidRaw : Array.isArray(pidRaw) ? String(pidRaw[0] || '') : ''
    if (!projectId) return

    if (to.fullPath === from.fullPath) return

    const navStore = useNavigationHistoryStore()
    navStore.recordProjectNavigation(projectId, to)
  } catch {
    // ignore
  }
})

export default router